// Cave84 — Wine catalog (Phase 1 Week 3)
// Spec §6.3 Screen 2. Searchable index of all wines with producer + appellation.
// Each row links to the Wine Analysis page (/wines/:wineId).
// Search is client-side over the full cached list (~230 wines).

import { useState, useMemo, useRef, useEffect } from 'react';
import { foldSearch } from '@/utils/searchText';
import { Link } from 'react-router-dom';
import { useWines, useDeleteWine, useUpdateWine } from '@/hooks/useWines';
import { useProducersMap } from '@/hooks/useProducers';
import { useAppellationsMap } from '@/hooks/useAppellations';
import type { Wine, WineStyle } from '@cave84/shared-types';
import { WishlistButton } from '@/components/WishlistButton';

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const STYLE_LABELS: Record<WineStyle, string> = {
  red: 'Red',
  white: 'White',
  rose: 'Rosé',
  sparkling: 'Sparkling',
  fortified: 'Fortified',
  sweet: 'Sweet',
  orange: 'Orange',
};

const STYLE_COLORS: Record<WineStyle, string> = {
  red: '#C49255',
  white: '#C49255',
  rose: '#D4768A',
  sparkling: '#7E9A98',
  fortified: '#8E663A',
  sweet: '#8FB57C',
  orange: '#C49255',
};

const ALL_STYLES: WineStyle[] = ['red', 'white', 'rose', 'sparkling', 'fortified', 'sweet', 'orange'];

function StyleDot({ style }: { style: WineStyle }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: STYLE_COLORS[style] }}
      title={STYLE_LABELS[style]}
    />
  );
}

function StyleFilterPill({
  style,
  active,
  count,
  onClick,
}: {
  style: WineStyle;
  active: boolean;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
        active
          ? 'border-transparent text-base'
          : 'border-border-medium text-text-secondary bg-surface-2 hover:border-border-strong'
      }`}
      style={active ? { backgroundColor: `${STYLE_COLORS[style]}22`, borderColor: STYLE_COLORS[style], color: STYLE_COLORS[style] } : {}}
    >
      <StyleDot style={style} />
      {STYLE_LABELS[style]}
      <span className="mono opacity-60">{count}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Per-row delete menu
// ---------------------------------------------------------------------------

function WineRowActions({ wine, producerName }: { wine: Wine; producerName: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const deleteWine = useDeleteWine();
  const updateWine = useUpdateWine();

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const wineName = [producerName, wine.cuvee_name].filter(Boolean).join(' — ');

  return (
    <>
      <div ref={menuRef} style={{ position:'relative', display:'inline-block' }}>
        <button
          type="button"
          onClick={e => { e.preventDefault(); e.stopPropagation(); setMenuOpen(v => !v); }}
          className="text-text-muted hover:text-text-secondary rounded px-1.5 py-1 transition-colors hover:bg-surface-2"
          title="Actions"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="5"  r="1.2" fill="currentColor"/>
            <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
            <circle cx="12" cy="19" r="1.2" fill="currentColor"/>
          </svg>
        </button>

        {menuOpen && (
          <div className="absolute right-0 z-50 mt-1 rounded border border-border-medium bg-surface-1 shadow-lg" style={{ minWidth:160, top:'calc(100% + 2px)' }}>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setMenuOpen(false); setEditNameValue(wine.cuvee_name ?? ''); setEditNameOpen(true); }}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-surface-2 transition-colors text-text-secondary"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit name
            </button>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setMenuOpen(false); setConfirmOpen(true); }}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-surface-2 transition-colors"
              style={{ color:'#E07070' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
              </svg>
              Delete wine
            </button>
          </div>
        )}
      </div>


      {editNameOpen && (
        <>
          <div role="button" tabIndex={0} aria-label="Close" className="fixed inset-0 z-50 bg-black/70" onClick={() => setEditNameOpen(false)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditNameOpen(false); } }}/>
          <div className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-md border border-border-medium bg-surface-1 p-8 shadow-2xl">
            <h3 className="font-display text-xl font-medium text-text-primary mb-1">Edit Cuvée Name</h3>
            <p className="text-xs text-text-muted mb-5">{producerName}</p>
            <input
              type="text"
              value={editNameValue}
              onChange={e => setEditNameValue(e.target.value)}
              className="w-full rounded border border-border-medium bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-gold mb-6"
              placeholder="e.g. Vosne-Romanée 1er Cru Les Suchots"
              ref={(el) => el?.focus()}
              onKeyDown={e => {
                if (e.key === 'Enter' && editNameValue.trim()) {
                  updateWine.mutate({ id: wine.id, patch: { cuvee_name: editNameValue.trim() } }, { onSuccess: () => setEditNameOpen(false) });
                }
                if (e.key === 'Escape') setEditNameOpen(false);
              }}
            />
            <div className="flex justify-end gap-2.5">
              <button type="button" onClick={() => setEditNameOpen(false)} disabled={updateWine.isPending}
                className="rounded border border-border-medium bg-transparent px-4 py-2 text-sm text-text-secondary hover:border-border-strong transition-colors">
                Cancel
              </button>
              <button type="button"
                onClick={() => updateWine.mutate({ id: wine.id, patch: { cuvee_name: editNameValue.trim() } }, { onSuccess: () => setEditNameOpen(false) })}
                disabled={updateWine.isPending || !editNameValue.trim()}
                className="rounded px-4 py-2 text-sm text-white transition-opacity"
                style={{ background:'#C49255', opacity: (updateWine.isPending || !editNameValue.trim()) ? 0.6 : 1, cursor: (updateWine.isPending || !editNameValue.trim()) ? 'default' : 'pointer', border:'none' }}>
                {updateWine.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}

      {confirmOpen && (
        <>
          <div role="button" tabIndex={0} aria-label="Close" className="fixed inset-0 z-50 bg-black/70" onClick={() => { deleteWine.reset(); setConfirmOpen(false); }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); deleteWine.reset(); setConfirmOpen(false); } }}/>
          <div className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-md border border-border-medium bg-surface-1 p-8 shadow-2xl">
            <h3 className="font-display text-xl font-medium text-text-primary mb-2">Delete Wine?</h3>
            <p className="text-sm text-text-secondary leading-relaxed mb-1.5">
              This permanently deletes the catalog entry <strong className="text-text-primary">{wineName}</strong>.
            </p>
            <p className="text-xs text-text-muted mb-6">Only possible when the wine has no bottles — none in the cellar and no drinking history. Bottles are never deleted here.</p>
            {deleteWine.error && (
              <p role="alert" className="text-sm leading-relaxed mb-6" style={{ color:'#E07070' }}>{deleteWine.error.message}</p>
            )}
            <div className="flex justify-end gap-2.5">
              <button type="button" onClick={() => { deleteWine.reset(); setConfirmOpen(false); }} disabled={deleteWine.isPending}
                className="rounded border border-border-medium bg-transparent px-4 py-2 text-sm text-text-secondary hover:border-border-strong transition-colors">
                Cancel
              </button>
              <button type="button"
                onClick={() => deleteWine.mutate(wine.id, { onSuccess: () => setConfirmOpen(false) })}
                disabled={deleteWine.isPending || !!deleteWine.error}
                className="rounded px-4 py-2 text-sm text-white transition-opacity"
                style={{ background:'#C49255', opacity: (deleteWine.isPending || deleteWine.error) ? 0.6 : 1, cursor: (deleteWine.isPending || deleteWine.error) ? 'default' : 'pointer', border:'none' }}>
                {deleteWine.isPending ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WinesPage() {
  const [search, setSearch] = useState('');
  const [activeStyle, setActiveStyle] = useState<WineStyle | null>(null);

  const { data: wines, isLoading, error } = useWines();
  const producersMap = useProducersMap();
  const appellationsMap = useAppellationsMap();

  // Client-side filter
  const filtered = useMemo(() => {
    if (!wines) return [];
    const lower = foldSearch(search.trim());
    return wines.filter(w => {
      if (activeStyle && w.style !== activeStyle) return false;
      if (!lower) return true;
      const producer = producersMap.get(w.producer_id)?.name ?? '';
      const cuvee = w.cuvee_name ?? '';
      const appellation = appellationsMap.get(w.appellation_id)?.name ?? '';
      return (
        foldSearch(producer).includes(lower) ||
        foldSearch(cuvee).includes(lower) ||
        foldSearch(appellation).includes(lower)
      );
    });
  }, [wines, search, activeStyle, producersMap, appellationsMap]);

  // Style counts for pills
  const styleCounts = useMemo(() => {
    const counts: Partial<Record<WineStyle, number>> = {};
    for (const w of wines ?? []) {
      counts[w.style] = (counts[w.style] ?? 0) + 1;
    }
    return counts;
  }, [wines]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const pa = producersMap.get(a.producer_id)?.name ?? '';
      const pb = producersMap.get(b.producer_id)?.name ?? '';
      if (pa !== pb) return pa.localeCompare(pb);
      return (a.cuvee_name ?? '').localeCompare(b.cuvee_name ?? '');
    });
  }, [filtered, producersMap]);

  return (
    <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="smallcaps text-text-tertiary mb-1">Catalog</p>
          <h1 className="font-display text-3xl font-light text-text-primary tracking-tight">
            Wine Analyses
          </h1>
        </div>
        {wines && (
          <p className="mono text-text-tertiary tabular-nums">
            {filtered.length.toLocaleString()}
            {filtered.length !== wines.length && (
              <span className="text-text-muted"> / {wines.length.toLocaleString()}</span>
            )}
            <span className="text-text-muted ml-1 smallcaps" style={{ fontSize: 11 }}>wines</span>
          </p>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <svg
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
        >
          <circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by producer, wine, or appellation…"
          className="w-full bg-surface-2 border border-border-medium rounded px-3 py-2.5 pl-9 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors"
        />
      </div>

      {/* Style filter pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {activeStyle && (
          <button
            type="button"
            onClick={() => setActiveStyle(null)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-border-medium text-text-tertiary text-xs hover:border-border-strong transition-colors"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3 h-3">
              <path d="M12 4 4 12M4 4l8 8" />
            </svg>
            Clear
          </button>
        )}
        {ALL_STYLES.filter(s => (styleCounts[s] ?? 0) > 0).map(s => (
          <StyleFilterPill
            key={s}
            style={s}
            active={activeStyle === s}
            count={styleCounts[s] ?? 0}
            onClick={() => setActiveStyle(activeStyle === s ? null : s)}
          />
        ))}
      </div>

      {/* Loading / error */}
      {isLoading && <p className="text-text-tertiary text-sm animate-pulse">Loading wines…</p>}
      {error && <p className="text-claret text-sm">Failed to load: {error.message}</p>}

      {/* Wine list */}
      {!isLoading && sorted.length > 0 && (
        <div className="overflow-x-auto">
        <div className="rounded-lg border border-border-subtle overflow-hidden min-w-[480px]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="text-left px-4 py-2.5 smallcaps text-text-muted font-normal" style={{ fontSize: 11 }}>Producer</th>
                <th className="text-left px-4 py-2.5 smallcaps text-text-muted font-normal" style={{ fontSize: 11 }}>Wine</th>
                <th className="text-left px-4 py-2.5 smallcaps text-text-muted font-normal hidden md:table-cell" style={{ fontSize: 11 }}>Appellation</th>
                <th className="text-right px-4 py-2.5 smallcaps text-text-muted font-normal" style={{ fontSize: 11 }}>Bottles</th>
                <th className="px-4 py-2.5 w-10" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((wine, i) => {
                const producer = producersMap.get(wine.producer_id);
                const appellation = appellationsMap.get(wine.appellation_id);
                return (
                  <tr
                    key={wine.id}
                    className={`border-b border-border-subtle last:border-0 hover:bg-surface-2 transition-colors ${i % 2 === 0 ? '' : 'bg-surface-1/50'}`}
                  >
                    <td className="px-4 py-3">
                      <span className="text-text-secondary">{producer?.name ?? wine.producer_id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/wines/${wine.id}`}
                        className="flex items-center gap-2 text-text-primary hover:text-gold transition-colors"
                      >
                        <StyleDot style={wine.style} />
                        <span className="font-display font-medium">
                          {wine.cuvee_name ?? '—'}
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-text-tertiary text-xs">
                        {appellation?.name ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="mono text-text-secondary tabular-nums">
                        {wine.bottle_count}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <WishlistButton
                          wineId={wine.id}
                          wineName={[producer?.name, wine.cuvee_name].filter(Boolean).join(' — ')}
                          size="xs"
                        />
                        <WineRowActions wine={wine} producerName={producer?.name ?? ''} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {!isLoading && sorted.length === 0 && wines && wines.length > 0 && (
        <div className="text-center py-16">
          <p className="font-display text-xl text-text-tertiary mb-1">No wines match</p>
                 <p className="text-text-muted text-sm">Try a different search or clear the filter.</p>
        </div>
      )}

      {!isLoading && (!wines || wines.length === 0) && (
        <div className="text-center py-16">
          <p className="font-display text-xl text-text-tertiary mb-1">No wines yet</p>
          <p className="text-text-muted text-sm">Add bottles to populate the catalog.</p>
        </div>
      )}
    </div>
  );
}
